if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        
        // Initialize height synchronization and vertical scrolling
        this.initThumbnailSlider();
        
        // Add mobile-specific event listeners for touch support
        this.addMobileEventListeners();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        
        // Check layout type
        const isThumbnailSliderLayout = this.dataset.desktopLayout.includes('thumbnail_slider');
        
        if (isThumbnailSliderLayout) {
          // Vertical scrolling for thumbnail_slider layout (both mobile and desktop)
          const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
          if (thumbnailList) {
            const thumbnailTop = thumbnail.offsetTop;
            thumbnailList.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          }
        } else {
          // Horizontal scrolling for other layouts
          if (this.elements.thumbnails.isSlideVisible && this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;
          
          // Use smooth scrolling
          if (this.elements.thumbnails.slider) {
            this.elements.thumbnails.slider.scrollTo({ 
              left: thumbnail.offsetLeft, 
              behavior: 'smooth' 
            });
          } else {
            // Fallback for direct scrolling
            this.elements.thumbnails.scrollTo({ 
              left: thumbnail.offsetLeft, 
              behavior: 'smooth' 
            });
          }
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      initThumbnailSlider() {
        const thumbnailSlider = this.elements.thumbnails.closest('.thumbnail-slider');
        const mainSlider = this.elements.viewer;
        
        if (!thumbnailSlider || !mainSlider) return;
        
        // Check if we're on mobile or desktop
        const isMobile = !this.mql.matches;
        const isThumbnailSliderLayout = this.dataset.desktopLayout.includes('thumbnail_slider');
        
        if (isThumbnailSliderLayout) {
          // Both mobile and desktop use vertical layout for thumbnail_slider
          this.syncThumbnailHeight();
          this.setupVerticalScrolling();
          
          // Add initialization class to show thumbnails after setup
          requestAnimationFrame(() => {
            thumbnailSlider.classList.add('js-initialized');
            // Ensure all thumbnails are visible after initialization
            this.showAllThumbnails();
          });
        } else if (isMobile) {
          // For mobile non-thumbnail_slider layouts, still add the class to prevent flash
          requestAnimationFrame(() => {
            thumbnailSlider.classList.add('js-initialized');
            // Ensure all thumbnails are visible after initialization
            this.showAllThumbnails();
          });
        }
        
        // Listen for window resize to maintain proper behavior
        window.addEventListener('resize', debounce(() => {
          if (isThumbnailSliderLayout) {
            this.syncThumbnailHeight();
          }
        }, 250));
      }

      syncThumbnailHeight() {
        const thumbnailSlider = this.elements.thumbnails.closest('.thumbnail-slider');
        const mainSlider = this.elements.viewer;
        
        if (!thumbnailSlider || !mainSlider) return;
        
        // Get the height of the main slider
        const mainSliderHeight = mainSlider.offsetHeight;
        
        // Apply the same height to thumbnail slider
        thumbnailSlider.style.height = mainSliderHeight + 'px';
        
        // Also set max-height for the thumbnail list to enable scrolling
        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (thumbnailList) {
          thumbnailList.style.maxHeight = mainSliderHeight + 'px';
          thumbnailList.style.overflowY = 'auto';
          thumbnailList.style.overflowX = 'hidden';
        }
      }

      setupVerticalScrolling() {
        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!thumbnailList) return;
        
        // Apply vertical layout styles
        thumbnailList.style.display = 'flex';
        thumbnailList.style.flexDirection = 'column';
        thumbnailList.style.gap = '0.5rem';
        thumbnailList.style.padding = '0.5rem';
        
        // Style thumbnail items for vertical layout
        const thumbnailItems = thumbnailList.querySelectorAll('.thumbnail-list__item');
        thumbnailItems.forEach(item => {
          item.style.width = '100%';
          item.style.flexShrink = '0';
          item.style.maxWidth = '150px';
          item.style.margin = '0 auto';
        });
        
        // Add custom scrollbar styling
        this.addScrollbarStyling(thumbnailList);
      }

      addScrollbarStyling(thumbnailList) {
        // Add CSS for custom scrollbar
        const style = document.createElement('style');
        style.textContent = `
          .thumbnail-slider .thumbnail-list::-webkit-scrollbar {
            width: 4px;
          }
          .thumbnail-slider .thumbnail-list::-webkit-scrollbar-track {
            background: rgba(var(--color-foreground), 0.1);
            border-radius: 2px;
          }
          .thumbnail-slider .thumbnail-list::-webkit-scrollbar-thumb {
            background: rgba(var(--color-foreground), 0.3);
            border-radius: 2px;
          }
          .thumbnail-slider .thumbnail-list::-webkit-scrollbar-thumb:hover {
            background: rgba(var(--color-foreground), 0.5);
          }
        `;
        document.head.appendChild(style);
      }

      showAllThumbnails() {
        const thumbnailList = this.elements.thumbnails.querySelector('.thumbnail-list');
        if (!thumbnailList) return;
        
        // Show all thumbnail items that might be hidden in fallback state
        const thumbnailItems = thumbnailList.querySelectorAll('.thumbnail-list__item');
        thumbnailItems.forEach(item => {
          item.style.display = 'block';
        });
      }


      addMobileEventListeners() {
        // Add orientation change listener for mobile
        window.addEventListener('orientationchange', debounce(() => {
          setTimeout(() => {
            this.initThumbnailSlider();
          }, 100);
        }, 250));

        // Add resize listener for better mobile responsiveness
        window.addEventListener('resize', debounce(() => {
          const isThumbnailSliderLayout = this.dataset.desktopLayout.includes('thumbnail_slider');
          if (isThumbnailSliderLayout) {
            this.syncThumbnailHeight();
          }
        }, 250));

        // Add touch-friendly interactions for mobile
        if (!this.mql.matches) {
          this.addMobileTouchInteractions();
        }
      }

      addMobileTouchInteractions() {
        const thumbnailButtons = this.elements.thumbnails.querySelectorAll('button');
        
        thumbnailButtons.forEach(button => {
          // Add touch feedback
          button.addEventListener('touchstart', (e) => {
            button.style.transform = 'scale(0.95)';
            button.style.transition = 'transform 0.1s ease';
          }, { passive: true });

          button.addEventListener('touchend', (e) => {
            button.style.transform = 'scale(1)';
          }, { passive: true });

          button.addEventListener('touchcancel', (e) => {
            button.style.transform = 'scale(1)';
          }, { passive: true });
        });
      }
    }
  );
}
